/**
 * Design tokens, transcribed from the artboards in `design/`.
 *
 * This file is **identical in all three Veriplus web repos and in the mobile
 * app**, up to the desktop-only block at the bottom. A value here exists in the
 * markup of an artboard. If a screen needs something this file does not have,
 * the design is the place to change it (upstream in `veri-plus-backend`, then
 * `./design/sync.sh`), not this file.
 *
 * The consumer half of the system is `veriplus-mobile-app/src/theme/tokens.ts`.
 * The first eleven colours below are byte-identical to it on purpose: one
 * product, one palette. Drift between the two is a bug in whichever moved.
 */

export const colors = {
  /** Actions only, never decoration. */
  brand: '#F1560A',
  /** Pressed / hover state for brand (from the artboard link style). */
  brandPressed: '#C74505',

  /** Ink and headers. */
  navy: '#17223B',
  /** The mobile camera screen, and the staff console sidebar. */
  scannerSurface: '#0C1322',

  /** Secondary text on light ground. */
  mutedInk: '#5A6478',
  /** Secondary text on navy. */
  mutedOnNavy: '#B9C0CF',

  /** App background. */
  ground: '#FAF8F5',
  /** Surfaces. */
  card: '#FFFFFF',
  /** Dividers, borders. */
  line: '#E8E2DA',

  disabledBg: '#E8E2DA',
  disabledFg: '#9AA0AE',

  white: '#FFFFFF',
} as const;

/**
 * Verdict semantics, never reuse these hues anywhere else.
 *
 * Brand orange is reserved for interactive elements so that an orange button
 * never reads as a warning. There are three verdicts, not a pass/fail pair:
 * `unknown` ("Not recognised") is a real outcome, not an error state. The
 * portal and the console report on the same three, so they use the same three.
 */
export const verdict = {
  genuine: { fg: '#147F47', bg: '#E5F4EC', border: '#B7E0C8', label: 'Genuine' },
  suspect: { fg: '#A16207', bg: '#FCF3D9', border: '#EBD9A9', label: 'Suspect' },
  unknown: { fg: '#D92D20', bg: '#FDEBE8', border: '#F3C1B8', label: 'Not recognised' },
} as const;

export type VerdictKind = keyof typeof verdict;

/**
 * Informational note panel. Mobile only: `#FDF6F1` and `#F6D9C6` are drawn on
 * the consumer artboards and by no desktop screen, so this repo does not carry
 * them. See `veriplus-mobile-app/src/theme/tokens.ts`.
 */

/** 4 - 8 - 12 - 16 - 24 - 32, screen gutter 20. */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  gutter: 20,
} as const;

export const radius = {
  card: 16,
  /** Buttons are pills. */
  pill: 26,
  chip: 12,
  input: 14,
} as const;

/*
 * ---------------------------------------------------------------------------
 * Desktop only, below this line.
 *
 * Every value here was counted across the 18 artboards on page-2 of the canvas
 * ("Portal & internal"). A hue became a token only where **three or more**
 * artboards draw it, which is the same test `veriplus-mobile-app` applied to
 * its type scale. The census and the rejected values are in
 * docs/DECISIONS.md D2, so that no later session re-derives them.
 * ---------------------------------------------------------------------------
 */

/** Chip grounds. Each is drawn with one fixed foreground, never mixed. */
export const chip = {
  /** 8 artboards. Brand-tinted status chip, text is `colors.brand`. */
  brand: { bg: '#FDE7DC', fg: colors.brand },
  /** 5 artboards. Neutral chip, text is `colors.navy`. */
  neutral: { bg: '#EAEDF4', fg: colors.navy },
  /** 4 artboards. Quiet chip, text is `colors.mutedInk`. */
  muted: { bg: '#F1EDE7', fg: colors.mutedInk },
} as const;

export type ChipKind = keyof typeof chip;

/**
 * Chart and map grounds.
 *
 * `plot` is the panel a chart or a heat map is drawn onto (3 artboards).
 * `mapLand` and `mapLandStroke` are the landmass fill and its outline on the
 * distribution and heat maps (3 artboards each, always as a pair).
 */
export const plot = {
  ground: '#F3EFE8',
  mapLand: '#EAE4D9',
  mapLandStroke: '#DED6C8',
} as const;

/**
 * The desktop shell.
 *
 * Drawn identically by all 16 screen artboards on page-2: a fixed 236px
 * sidebar against a 1440x900 frame, 32px/36px main padding. The sidebar ground
 * is the one thing the two surfaces do not share, and the difference is
 * load-bearing: the manufacturer portal is navy, the staff console is the
 * darker scanner surface, so a screenshot says which system you are in before
 * you read a word. Do not normalise them.
 */
export const shell = {
  sidebarWidth: 236,
  sidebarPadding: { y: 24, x: 16 },
  /** Nav row: 11px/14px padding, 12px radius, 4px gap, 12px icon gap. */
  navRow: { paddingY: 11, paddingX: 14, radius: radius.chip, gap: 4, iconGap: 12 },
  mainPadding: { y: 32, x: 36 },
  mainGap: 24,
  /** The artboards are drawn at this frame. It is a drawing size, not a min-width. */
  artboard: { width: 1440, height: 900 },
  /** Primary action button in a page header: 44px tall, 22px radius, 0/22 padding. */
  actionButton: { height: 44, radius: 22, paddingX: 22 },
  ground: {
    /** manufacturer.veri-plus.com */
    portal: colors.navy,
    /** ops.veri-plus.org */
    console: colors.scannerSurface,
  },
  /** The sidebar footer rule, the only rgba in the design. */
  sidebarDivider: 'rgba(255, 255, 255, 0.14)',
} as const;
